import {
  processResponse,
  checkIfLinkHasProtocol,
  removeStartingSlash
} from '@/plugins/helper'
import {
  findArticleCategorySlugByCategorySlug,
  findMigratedCategorySlug
} from 'enums/categories'
import { DEFAULT_FALLBACK_LINK } from 'enums/links'
import { checkIfMigratedTag } from 'enums/tags'
import { findCompanyCategorySlugByCategorySlug } from 'enums/directory-categories'

export function generateIlUrl(link, ctx) {
  return generateFullUrl(link, ctx.$env.IL_DOMAIN_URL)
}

export function generateFmUrl(link, ctx) {
  return generateFullUrl(link, ctx.$env.IL_FM_DOMAIN_URL)
}

export function generateFmDirUrl(link, ctx) {
  return generateFullUrl(link, ctx.$env.IL_FM_DIR_DOMAIN_URL)
}

export function generateFullUrl(link, defaultDomain) {
  if (!link) return defaultDomain

  const linkWithoutStartSlash = removeStartingSlash(link)
  return checkIfLinkHasProtocol(linkWithoutStartSlash)
    ? link
    : `${defaultDomain}/${linkWithoutStartSlash}`
}

export function generateArticlePath({ slug, categorySlug, url, isMigrated }) {
  if (url) return url

  if (!categorySlug || !slug) return DEFAULT_FALLBACK_LINK

  const handledCategorySlug =
    findArticleCategorySlugByCategorySlug(categorySlug) || categorySlug

  const articlePath = isMigrated
    ? `/${handledCategorySlug}/!/${slug}`
    : `/${handledCategorySlug}/${slug}/`

  return articlePath
}

export function generateArticleUrl(article, ctx) {
  return generateIlUrl(generateArticlePath(processResponse(article)), ctx)
}

export function generateAuthorPath(author) {
  if (!author) return null

  const { slug } = processResponse(author)
  return `/author/${slug}`
}

export function generateAuthorUrl(author, ctx) {
  return generateIlUrl(generateAuthorPath(author), ctx)
}

export function generateCategoryPath(category) {
  const { slug } = processResponse(category)
  const oldCategorySlug = findMigratedCategorySlug(slug)

  return oldCategorySlug ? `/${oldCategorySlug}` : `/${slug}/`
}

export function generateCategoryUrl(category, ctx) {
  return generateIlUrl(generateCategoryPath(category), ctx)
}

export function generateTagPath(tag = {}) {
  const { slug } = processResponse(tag)

  if (!slug) return DEFAULT_FALLBACK_LINK

  return checkIfMigratedTag(slug)
    ? `/Tag/${slug.toLowerCase()}`
    : `/Tag/${slug.toLowerCase()}/`
}

export function generateTagUrl(tag, ctx) {
  return generateIlUrl(generateTagPath(tag), ctx)
}

export function generateTermPath(params = {}) {
  const formattedParams = processResponse(params)
  const { slug: termSlug } = formattedParams

  if (!termSlug) return DEFAULT_FALLBACK_LINK

  return `/terms/${termSlug.charAt(0)}/${termSlug}/`
}

export function generateTermUrl(params = {}, ctx) {
  return generateIlUrl(generateTermPath(params), ctx)
}

export function generateCompanyPath(company = {}) {
  const { slug, categorySlug } = processResponse(company)
  if (!slug || !categorySlug) return null

  const handledCategorySlug =
    findCompanyCategorySlugByCategorySlug(categorySlug) || categorySlug

  return `/${handledCategorySlug}/${slug}`
}

export function generateCompanyUrl(company, ctx) {
  return generateIlUrl(generateCompanyPath(company), ctx)
}

export function generateIlArticlePath(article, { amp = false } = {}) {
  const processedArticle = processResponse(article)

  if (processedArticle.path || processedArticle.url) {
    return processedArticle.path || processedArticle.url
  }

  const { slug, isMigrated, categorySlug } = processedArticle

  if (!categorySlug || !slug) return DEFAULT_FALLBACK_LINK

  const handledCategorySlug =
    findArticleCategorySlugByCategorySlug(categorySlug) || categorySlug

  return isMigrated
    ? `/${handledCategorySlug}/!/${slug}${amp ? '/amp/' : ''}`
    : `/${handledCategorySlug}/${slug}/${amp ? 'amp/' : ''}`
}

export function generateIlAuthorPath(authorSlug) {
  if (!authorSlug) return DEFAULT_FALLBACK_LINK

  return `/author/${authorSlug}`
}

export function generateIlCompanyPath(company) {
  const { slug, categorySlug } = processResponse(company)
  if (!slug || !categorySlug) return DEFAULT_FALLBACK_LINK

  const processedCategorySlug =
    findCompanyCategorySlugByCategorySlug(categorySlug) || categorySlug

  return `/${processedCategorySlug}/${slug}/`
}

export function generateFmDirArticlePath(article) {
  const processedArticle = processResponse(article)

  if (processedArticle.path || processedArticle.url) {
    return processedArticle.path || processedArticle.url
  }

  if (!article.categorySlug) {
    console.log(
      '%cCategorySlug not found from backend!',
      'color: black ; background-color: cyan'
    )
  }

  const { categorySlug, companySlug } = processedArticle
  const articleSlug = processedArticle.slug

  return `/${categorySlug}/${companySlug}/${articleSlug}`
}

export function generateFmDirCompanyUrl(company, ctx) {
  const { slug, categorySlug } = processResponse(company)

  if (!slug || !categorySlug) return null

  const link = `/${categorySlug}/${slug}/`
  return generateFmDirUrl(link, ctx)
}
